<c-container fluid>
  <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
    <button cButton color="secondary" variant="outline" size="sm" (click)="back()">‹ {{ 'card_partner_admin.back' | translate }}</button>
    <h4 class="mb-0 me-auto text-break">{{ account()?.email || partnerUserId }}</h4>
    @if (detail()) {
      <button cButton color="primary" size="sm" [disabled]="refreshing()" (click)="refresh()">
        @if (refreshing()) { <c-spinner size="sm" class="me-1"></c-spinner> }
        {{ 'card_partner_admin.refresh' | translate }}
      </button>
    }
  </div>
  @if (refreshHint(); as hint) {
    <div class="alert py-2 small" [class.alert-success]="hint === 'card_partner_admin.refresh_done'" [class.alert-warning]="hint !== 'card_partner_admin.refresh_done'">
      {{ hint | translate }}
    </div>
  }

  @if (loading()) {
    <div class="text-center py-4"><c-spinner></c-spinner></div>
  } @else if (!detail()) {
    <div class="alert alert-danger">{{ (notFound() ? 'card_partner_admin.not_found' : 'card_partner_admin.load_error') | translate }}</div>
  }

  @if (detail(); as d) {
    <c-row class="g-3 mb-3">
      <!-- Account -->
      <c-col xs="12" lg="7">
        <c-card class="h-100">
          <c-card-header><strong>{{ 'card_partner_admin.account_title' | translate }}</strong></c-card-header>
          <c-card-body>
            <div class="d-flex flex-wrap gap-4 mb-3">
              <div>
                <div class="small text-medium-emphasis">{{ 'mycards.partner.account_status' | translate }}</div>
                <c-badge [color]="accountColor(d.account.account_status)">{{ keyOr('account', d.account.account_status) | translate }}</c-badge>
              </div>
              <div>
                <div class="small text-medium-emphasis">{{ 'mycards.partner.kyc_status' | translate }}</div>
                <c-badge [color]="kycColor(d.account.kyc_status)">{{ keyOr('kyc', d.account.kyc_status) | translate }}</c-badge>
              </div>
              <div>
                <div class="small text-medium-emphasis">{{ 'mycards.partner.card_activated' | translate }}</div>
                <div>{{ d.account.card_activated_at ? dateTime(d.account.card_activated_at) : ('mycards.partner.not_yet' | translate) }}</div>
              </div>
            </div>

            @if (d.account.kyc_reason || d.account.kyc_reason_category) {
              <div class="alert alert-warning small py-2">
                <strong>{{ 'mycards.partner.kyc_reason' | translate }}:</strong>
                @if (d.account.kyc_reason_category) { <c-badge color="secondary" class="ms-1">{{ d.account.kyc_reason_category }}</c-badge> }
                {{ d.account.kyc_reason }}
                <div class="mt-1 text-medium-emphasis">{{ 'card_partner_admin.kyc_reason_note' | translate }}</div>
              </div>
            }

            <div class="table-scroll">
              <table cTable [small]="true" class="mb-0 kv-table">
                <tbody>
                  <tr><th>{{ 'card_partner_admin.field_partner_user_id' | translate }}</th><td><code>{{ d.account.partner_user_id }}</code></td></tr>
                  <tr><th>{{ 'card_partner_admin.field_email' | translate }}</th><td class="text-break">{{ d.account.email || '—' }}</td></tr>
                  <tr><th>{{ 'card_partner_admin.field_nickname' | translate }}</th><td>{{ d.account.nickname || '—' }}</td></tr>
                  <tr><th>{{ 'card_partner_admin.field_card_holder_id' | translate }}</th><td><code>{{ d.account.card_holder_id || '—' }}</code></td></tr>
                  <tr><th>{{ 'card_partner_admin.field_account_id' | translate }}</th><td><code>{{ d.account.account_id || '—' }}</code></td></tr>
                  <tr><th>{{ 'card_partner_admin.field_kyc_step' | translate }}</th><td>{{ d.account.kyc_current_step || '—' }}</td></tr>
                  <tr>
                    <th>{{ 'card_partner_admin.field_kyc_completed' | translate }}</th>
                    <td>
                      @for (step of d.account.kyc_completed_steps; track step) { <c-badge color="secondary" class="me-1">{{ step }}</c-badge> }
                      @empty { — }
                    </td>
                  </tr>
                  <tr><th>{{ 'card_partner_admin.field_referral_code' | translate }}</th><td><code>{{ d.account.referral_code || '—' }}</code></td></tr>
                  <tr>
                    <th>{{ 'card_partner_admin.field_referrer' | translate }}</th>
                    <td>
                      @if (d.referrer; as ref) {
                        <a href="#" (click)="$event.preventDefault(); openAccount(ref.partner_user_id)">{{ ref.email || ref.partner_user_id }}</a>
                      } @else {
                        {{ d.account.referrer_id || '—' }}
                      }
                    </td>
                  </tr>
                  <tr><th>{{ 'card_partner_admin.field_created' | translate }}</th><td>{{ dateTime(d.account.partner_created_at) }}</td></tr>
                  <tr><th>{{ 'card_partner_admin.field_synced' | translate }}</th><td>{{ dateTime(d.account.synced_at) }}</td></tr>
                  <tr><th>{{ 'card_partner_admin.field_details_synced' | translate }}</th><td>{{ dateTime(d.account.details_synced_at) }}</td></tr>
                </tbody>
              </table>
            </div>
          </c-card-body>
        </c-card>
      </c-col>

      <c-col xs="12" lg="5">
        <!-- Our user -->
        <c-card class="mb-3">
          <c-card-header><strong>{{ 'card_partner_admin.our_user_title' | translate }}</strong></c-card-header>
          <c-card-body>
            @if (d.user; as u) {
              <div><a href="#" (click)="$event.preventDefault(); openUser(u.id)">#{{ u.id }}</a> · {{ u.full_name || '—' }}</div>
              <div class="small text-break">{{ u.email }}</div>
              @if (u.email.toLowerCase() !== (d.account.email || '').toLowerCase()) {
                <div class="small text-warning mt-1">{{ 'card_partner_admin.email_differs' | translate }}</div>
              }
            } @else {
              <div class="text-medium-emphasis">{{ 'card_partner_admin.not_linked' | translate }}</div>
            }
          </c-card-body>
        </c-card>

        <!-- Totals -->
        <c-card>
          <c-card-header><strong>{{ 'card_partner_admin.totals_title' | translate }}</strong></c-card-header>
          <c-card-body>
            <div class="table-scroll">
              <table cTable [small]="true" class="mb-0">
                <thead>
                  <tr>
                    <th></th>
                    @for (r of totalsRows(); track r.key) {
                      <th class="text-end text-nowrap">{{ r.key === 'period' ? d.period.month : ('card_partner_admin.all_time' | translate) }}</th>
                    }
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>{{ 'card_partner_admin.kpi_spend' | translate }} (USD)</th>
                    @for (r of totalsRows(); track r.key) { <td class="text-end text-nowrap">{{ money(r.totals.spend) }}</td> }
                  </tr>
                  <tr>
                    <th>{{ 'card_partner_admin.kpi_fees' | translate }} (USD)</th>
                    @for (r of totalsRows(); track r.key) { <td class="text-end text-nowrap">{{ money(r.totals.fees) }}</td> }
                  </tr>
                  <tr>
                    <th>{{ 'card_partner_admin.col_tx_count' | translate }}</th>
                    @for (r of totalsRows(); track r.key) { <td class="text-end">{{ r.totals.tx_count }}</td> }
                  </tr>
                  <tr>
                    <th>{{ 'card_partner_admin.kpi_deposits' | translate }}</th>
                    @for (r of totalsRows(); track r.key) {
                      <td class="text-end text-nowrap">
                        @for (e of tokenEntries(r.totals.deposits); track e.token) { <div>{{ crypto(e.value) }} {{ e.token }}</div> } @empty { — }
                      </td>
                    }
                  </tr>
                  <tr>
                    <th>{{ 'card_partner_admin.col_withdrawals' | translate }}</th>
                    @for (r of totalsRows(); track r.key) {
                      <td class="text-end text-nowrap">
                        @for (e of tokenEntries(r.totals.withdrawals); track e.token) { <div>{{ crypto(e.value) }} {{ e.token }}</div> } @empty { — }
                      </td>
                    }
                  </tr>
                  <tr>
                    <th>{{ 'card_partner_admin.withdrawal_fees' | translate }}</th>
                    @for (r of totalsRows(); track r.key) { <td class="text-end text-nowrap">{{ crypto(r.totals.withdrawal_fees) }}</td> }
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="small text-medium-emphasis mt-2">{{ 'card_partner_admin.spend_hint' | translate }}</div>
          </c-card-body>
        </c-card>
      </c-col>
    </c-row>

    <!-- Cards -->
    <c-card class="mb-3">
      <c-card-header><strong>{{ 'card_partner_admin.cards_title' | translate }}</strong></c-card-header>
      <c-card-body>
        @if (d.cards.length === 0) {
          <div class="text-medium-emphasis">{{ 'mycards.partner.no_cards' | translate }}</div>
        } @else {
          <div class="table-scroll">
            <table cTable [small]="true" class="mb-0 align-middle">
              <thead>
                <tr>
                  <th>{{ 'card_partner_admin.col_card' | translate }}</th>
                  <th>{{ 'mycards.partner.status' | translate }}</th>
                  <th>{{ 'mycards.partner.physical_card' | translate }}</th>
                  <th>{{ 'mycards.partner.ordered_at' | translate }}</th>
                  <th>{{ 'mycards.partner.distributed_at' | translate }}</th>
                  <th>{{ 'card_partner_admin.field_created' | translate }}</th>
                  <th>{{ 'card_partner_admin.field_card_id' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                @for (c of d.cards; track c.id) {
                  <tr>
                    <td><code>•••• {{ c.last_four || '????' }}</code></td>
                    <td><c-badge [color]="cardColor(c.card_status)">{{ keyOr('card', c.card_status) | translate }}</c-badge></td>
                    <td>{{ (c.physical_status ? keyOr('physical', c.physical_status) : 'mycards.partner.physical_none') | translate }}</td>
                    <td class="text-nowrap">{{ date(c.physical_ordered_at) }}</td>
                    <td class="text-nowrap">{{ date(c.physical_distributed_at) }}</td>
                    <td class="text-nowrap">{{ date(c.created_at) }}</td>
                    <td><code class="small">{{ c.id }}</code></td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </c-card-body>
    </c-card>

    <!-- Deposit addresses (all rows) -->
    <c-card class="mb-3">
      <c-card-header><strong>{{ 'mycards.partner.addresses_title' | translate }}</strong> <span class="small text-medium-emphasis ms-2">{{ d.deposit_addresses.length }}</span></c-card-header>
      <c-card-body>
        @if (d.deposit_addresses.length === 0) {
          <div class="text-medium-emphasis">{{ 'mycards.partner.no_addresses' | translate }}</div>
        } @else {
          <div class="table-scroll">
            <table cTable [small]="true" class="mb-0 align-middle">
              <thead>
                <tr>
                  <th>{{ 'mycards.partner.network' | translate }}</th>
                  <th>{{ 'card_partner_admin.col_token' | translate }}</th>
                  <th>{{ 'card_partner_admin.col_address' | translate }}</th>
                  <th>{{ 'mycards.partner.memo' | translate }}</th>
                  <th>{{ 'card_partner_admin.col_registered' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                @for (a of d.deposit_addresses; track a.network + a.token) {
                  <tr [class.text-medium-emphasis]="!a.registered">
                    <td class="text-nowrap">{{ networkLabel(a.network) }}</td>
                    <td>{{ a.token }}</td>
                    <td>
                      @if (a.address) {
                        <code class="small text-break">{{ a.address }}</code>
                        <button cButton color="secondary" variant="ghost" size="sm" class="ms-1 py-0" (click)="copy(a.address)">
                          {{ (copied() === a.address ? 'mycards.partner.copied' : 'mycards.partner.copy') | translate }}
                        </button>
                      } @else { — }
                    </td>
                    <td>{{ a.memo || '—' }}</td>
                    <td class="text-nowrap">
                      @if (a.registered) {
                        <c-badge color="success">{{ 'card_partner_admin.answer_yes' | translate }}</c-badge>
                        <span class="small ms-1">{{ date(a.first_registered_at) }}</span>
                      } @else {
                        <c-badge color="secondary">{{ 'card_partner_admin.answer_no' | translate }}</c-badge>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          <div class="small text-medium-emphasis mt-2">{{ 'card_partner_admin.addresses_hint' | translate }}</div>
        }
      </c-card-body>
    </c-card>

    <!-- Transactions -->
    <c-card class="mb-3">
      <c-card-header>
        <strong>{{ 'mycards.partner.tx_title' | translate }}</strong>
        <span class="small text-medium-emphasis ms-2">{{ transactions().total }}</span>
      </c-card-header>
      <c-card-body>
        @if (transactions().items.length === 0) {
          <div class="text-medium-emphasis">{{ (transactions().loading ? 'card_partner_admin.loading' : 'mycards.partner.no_tx') | translate }}</div>
        } @else {
          <div class="table-scroll" [class.opacity-50]="transactions().loading">
            <table cTable [small]="true" class="mb-0 align-middle">
              <thead>
                <tr>
                  <th>{{ 'mycards.partner.date' | translate }}</th>
                  <th>{{ 'card_partner_admin.col_type' | translate }}</th>
                  <th>{{ 'mycards.partner.description' | translate }}</th>
                  <th>MCC</th>
                  <th class="text-end">{{ 'mycards.partner.amount' | translate }}</th>
                  <th class="text-end">{{ 'mycards.partner.fee' | translate }}</th>
                  <th>{{ 'mycards.partner.status' | translate }}</th>
                  <th>{{ 'card_partner_admin.col_card' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                @for (t of transactions().items; track t.id) {
                  <tr>
                    <td class="text-nowrap">{{ dateTime(t.time) }}</td>
                    <td class="text-nowrap">{{ keyOr('type', t.type) | translate }}</td>
                    <td>
                      {{ t.merchant_name || '—' }}
                      @if (txPlace(t)) { <div class="small text-medium-emphasis">{{ txPlace(t) }}</div> }
                    </td>
                    <td class="small">{{ t.mcc_category || t.mcc || '—' }}</td>
                    <td class="text-end text-nowrap" [class.text-success]="txDirection(t) === 'in'">
                      {{ txAmount(t) }}
                      @if (txOriginal(t); as orig) { <div class="small text-medium-emphasis">{{ orig }}</div> }
                    </td>
                    <td class="text-end text-nowrap">{{ t.fee ? money(t.fee) : '—' }}</td>
                    <td><c-badge [color]="statusColor(t.status)">{{ keyOr('tx_status', t.status) | translate }}</c-badge></td>
                    <td><code class="small">{{ t.card_last_four || '—' }}</code></td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          <ng-container *ngTemplateOutlet="pager; context: { kind: 'transactions', state: transactions() }"></ng-container>
        }
      </c-card-body>
    </c-card>

    <!-- Deposits + withdrawals -->
    @for (block of [{ kind: 'deposits', state: deposits(), title: 'mycards.partner.deposits_title', empty: 'mycards.partner.no_deposits' },
                    { kind: 'withdrawals', state: withdrawals(), title: 'mycards.partner.withdrawals_title', empty: 'mycards.partner.no_withdrawals' }]; track block.kind) {
      <c-card class="mb-3">
        <c-card-header>
          <strong>{{ block.title | translate }}</strong>
          <span class="small text-medium-emphasis ms-2">{{ block.state.total }}</span>
        </c-card-header>
        <c-card-body>
          @if (block.state.items.length === 0) {
            <div class="text-medium-emphasis">{{ (block.state.loading ? 'card_partner_admin.loading' : block.empty) | translate }}</div>
          } @else {
            <div class="table-scroll" [class.opacity-50]="block.state.loading">
              <table cTable [small]="true" class="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>{{ 'mycards.partner.date' | translate }}</th>
                    <th>{{ 'mycards.partner.network' | translate }}</th>
                    <th class="text-end">{{ 'mycards.partner.amount' | translate }}</th>
                    <th class="text-end">{{ 'mycards.partner.fee' | translate }}</th>
                    <th>{{ 'mycards.partner.status' | translate }}</th>
                    <th>{{ 'mycards.partner.tx_hash' | translate }}</th>
                    <th>{{ (block.kind === 'deposits' ? 'card_partner_admin.col_from' : 'card_partner_admin.col_to') | translate }}</th>
                  </tr>
                </thead>
                <tbody>
                  @for (x of block.state.items; track x.id) {
                    <tr>
                      <td class="text-nowrap">{{ dateTime(x.time) }}</td>
                      <td class="text-nowrap">{{ chainLabel(x.chain) }}</td>
                      <td class="text-end text-nowrap">{{ crypto(x.amount) }} {{ x.currency }}</td>
                      <td class="text-end text-nowrap">{{ crypto(x.fee) }}</td>
                      <td>
                        <c-badge [color]="statusColor(x.status)">{{ (x.status ? keyOr('transfer_status', x.status) : 'card_partner_admin.status_unknown') | translate }}</c-badge>
                        @if (x.ladder_status) { <div class="small text-medium-emphasis">{{ x.ladder_status }}</div> }
                      </td>
                      <td>
                        @if (x.transaction_hash) {
                          <code class="small" [title]="x.transaction_hash">{{ shortHash(x.transaction_hash) }}</code>
                          <button cButton color="secondary" variant="ghost" size="sm" class="ms-1 py-0" (click)="copy(x.transaction_hash)">
                            {{ (copied() === x.transaction_hash ? 'mycards.partner.copied' : 'mycards.partner.copy') | translate }}
                          </button>
                        } @else { — }
                      </td>
                      <td>
                        @if (block.kind === 'deposits' ? x.from_address : x.to_address; as addr) {
                          <code class="small" [title]="addr">{{ shortHash(addr) }}</code>
                        } @else { — }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            <ng-container *ngTemplateOutlet="pager; context: { kind: block.kind, state: block.state }"></ng-container>
          }
        </c-card-body>
      </c-card>
    }
  }

  <ng-template #pager let-kind="kind" let-state="state">
    @if (state.totalPages > 1) {
      <div class="d-flex flex-wrap align-items-center justify-content-center gap-2 mt-3">
        <button cButton color="secondary" variant="outline" size="sm" [disabled]="state.page <= 1 || state.loading" (click)="loadList(kind, state.page - 1)">‹</button>
        <span class="small">{{ 'card_partner_admin.page_of' | translate: { page: state.page, pages: state.totalPages } }}</span>
        <button cButton color="secondary" variant="outline" size="sm" [disabled]="state.page >= state.totalPages || state.loading" (click)="loadList(kind, state.page + 1)">›</button>
      </div>
    }
  </ng-template>
</c-container>
